import { isUndefined } from '@aloft/toolkit-shared';
import { Block, Button, Flex, Icon, Tooltip, Typography, colors } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import { DateOfSaleMethodInput } from '../../redux/features/selectors/calculatedMarketAdjustmentSelectors/selectDateOfSaleOrListToSaleAdjustment.ts';
import {
  getZillowMethodChartDisplay,
  getZillowMethodTableDisplay,
} from '../../redux/features/selectors/calculatedMarketAdjustmentSelectors/selectMarketAdjustmentsDisplay.ts';
import { CommentaryDateOfSaleTable } from '../commentary/components/CommentaryDateOfSaleTable.tsx';
import { ZillowHomeValueIndexTrendsChartUnwrapped } from '../marketConditions/zillow/ZillowHomeValueIndexTrendsChart.tsx';
import { MarketAdjustmentsModalForm } from './types.ts';

type DataView = 'chart' | 'table';

type Props = {
  input: DateOfSaleMethodInput.Zillow | undefined;
};

const tooltipLabel =
  'The Zillow Home Value Index (ZHVI) provides a Typical Home Value, the weighted average of the middle-third of homes in a given region (33rd-66th percentile). It leverages Zestimates from over 100 million homes, including new constructions and properties not recently sold, providing a broader perspective than indices based solely on sold properties. The subset of data shown here includes historical data from the "ZHVI Single-Family Homes Time Series" and a projection using the "ZHVF (forecast), All Homes (SFR, Condo/Coop), Raw, Mid-Tier (MoM%)" for the most recent published month.';

export function ZillowMethodContent({ input }: Props) {
  const [view, setView] = useState<DataView>('chart');

  const analysisPeriod = useWatch<MarketAdjustmentsModalForm>({
    name: 'marketAdjustments.cachedForms.dateOfSale.methodState.zillow.analysisPeriod',
  }) as 6 | 12 | 24; // not sure why type isn't getting inferred correctly

  if (isUndefined(input)) {
    return <Typography>Not enough Zillow data in zip code to use Zillow adjustments.</Typography>;
  }

  const chartData = getZillowMethodChartDisplay({ input, analysisPeriod });
  const tableData = getZillowMethodTableDisplay({ input, analysisPeriod, maxRowSize: 24 });

  return (
    <Block width="100%" position="relative">
      <Flex
        width="100%"
        className="flex"
        justifyContent="space-between"
        alignItems="baseline"
        position="sticky"
        top={0}
        backgroundColor="surface1"
        zIndex="1"
        marginBottom="16px"
      >
        <Flex>
          <Typography variant="h3">
            Zillow Home Value Index ({input.zip || 'Unknown Zip Code'})
          </Typography>
          <Tooltip label={tooltipLabel}>
            <Flex marginLeft="2px">
              <Icon size="sm" icon={'question'} color="textSecondary" />
            </Flex>
          </Tooltip>
        </Flex>
        <Flex gap="16px" alignItems="center" justify="flex-end">
          <Button
            text="Chart"
            onClick={() => setView('chart')}
            variant="inline"
            __dangerDoNotUseSx={{
              color: view === 'chart' ? colors.textPrimary : colors.textTertiary,
            }}
          />
          <Button
            text="Table"
            onClick={() => setView('table')}
            variant="inline"
            __dangerDoNotUseSx={{
              color: view === 'table' ? colors.textPrimary : colors.textTertiary,
            }}
          />
          <Block width="1px" height="21px" backgroundColor="strokeDefault" />
          <Controller<MarketAdjustmentsModalForm>
            name="marketAdjustments.cachedForms.dateOfSale.methodState.zillow.analysisPeriod"
            render={({ field }) => (
              <Button
                text="6 months"
                onClick={() => field.onChange(6)}
                variant="inline"
                __dangerDoNotUseSx={{
                  color: analysisPeriod === 6 ? colors.textPrimary : colors.textTertiary,
                }}
              />
            )}
          />
          <Controller<MarketAdjustmentsModalForm>
            name="marketAdjustments.cachedForms.dateOfSale.methodState.zillow.analysisPeriod"
            render={({ field }) => (
              <Button
                text="12 months"
                onClick={() => field.onChange(12)}
                variant="inline"
                __dangerDoNotUseSx={{
                  color: analysisPeriod === 12 ? colors.textPrimary : colors.textTertiary,
                }}
              />
            )}
          />
          <Controller<MarketAdjustmentsModalForm>
            name="marketAdjustments.cachedForms.dateOfSale.methodState.zillow.analysisPeriod"
            render={({ field }) => (
              <Button
                text="24 months"
                onClick={() => field.onChange(24)}
                variant="inline"
                __dangerDoNotUseSx={{
                  color: analysisPeriod === 24 ? colors.textPrimary : colors.textTertiary,
                }}
              />
            )}
          />
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap="12px">
        {view === 'table' && tableData?.[0] && (
          <Block overflowX="auto">
            <CommentaryDateOfSaleTable data={tableData[0]} />
          </Block>
        )}
        {view === 'chart' && chartData && (
          <ZillowHomeValueIndexTrendsChartUnwrapped {...chartData} />
        )}
        <Typography>
          The Zillow Home Value Index for a given month is published within weeks of a month ending.
          The date of sale adjustments reflect the difference between the Typical Home Value of
          Single Family Homes each month compared to the month of the effective date of the
          appraisal. If values have not been released for the current month, adjustments are based
          on the prior month.
        </Typography>
      </Flex>
    </Block>
  );
}
